import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { buildTree, HierarchyTreeView } from '@/components/HierarchyTree';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Upload, Save, Link2, Database, Check, GitBranch, Download } from 'lucide-react';
import { toast } from 'sonner';

function StatsRow({ codes, tree, usageCount }) {
  return (
    <div className="grid grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-6" data-testid="vs-stats">
      {[
        { label: 'Total Codes', value: codes.length },
        { label: 'Root Level', value: tree.length },
        { label: 'Used In', value: `${usageCount} schemas` },
      ].map(s => (
        <div key={s.label} className="bg-white p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold mb-1">{s.label}</div>
          <div className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

function UsagesSidebar({ usages, navigate }) {
  if (!usages || usages.length === 0) return null;
  return (
    <div className="w-64 shrink-0">
      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
        <Link2 className="w-3 h-3 inline mr-1" /> Schema Usages
      </h2>
      <div className="border border-[#E5E7EB]" data-testid="vs-usages">
        {usages.map((u, i) => (
          <button key={i} onClick={() => navigate(`/schemas/${u.schema_id}`)} className="w-full text-left px-3 py-2 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB] transition-colors" data-testid={`usage-${i}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <Database className="w-3 h-3 text-[#002FA7]" strokeWidth={1.5} />
              <span className="text-xs font-medium text-[#002FA7]">{u.schema_name}</span>
            </div>
            <span className="text-[10px] text-[#9CA3AF]">{u.attribute_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CsvPreview({ csvParsed, csvMode, setCsvMode, onApply }) {
  return (
    <div className="mt-2 space-y-4">
      <p className="text-sm text-[#4B5563]">
        Parsed <strong>{csvParsed.count}</strong> codes.
        Columns: {csvParsed.columns_detected?.join(', ')}
      </p>
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-[#4B5563]">Import mode:</span>
        <button onClick={() => setCsvMode('merge')} className={`text-xs px-3 py-1.5 border transition-colors ${csvMode === 'merge' ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]' : 'border-[#E5E7EB] text-[#4B5563]'}`} data-testid="csv-mode-merge">
          Merge (keep existing, add new)
        </button>
        <button onClick={() => setCsvMode('replace')} className={`text-xs px-3 py-1.5 border transition-colors ${csvMode === 'replace' ? 'border-red-500 bg-red-50 text-red-600' : 'border-[#E5E7EB] text-[#4B5563]'}`} data-testid="csv-mode-replace">
          Replace all
        </button>
      </div>
      <div className="border border-[#E5E7EB] max-h-64 overflow-auto" data-testid="csv-preview">
        <div className="grid grid-cols-12 bg-[#F9FAFB] px-3 py-1.5 border-b border-[#E5E7EB] sticky top-0">
          <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Code</div>
          <div className="col-span-6 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Label</div>
          <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Parent</div>
        </div>
        {csvParsed.codes.slice(0, 50).map((c, i) => (
          <div key={i} className="grid grid-cols-12 px-3 py-1 border-b border-[#E5E7EB] last:border-b-0">
            <div className="col-span-3 text-xs font-medium" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.code}</div>
            <div className="col-span-6 text-xs text-[#4B5563]">{c.label}</div>
            <div className="col-span-3 text-[10px] text-[#9CA3AF]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.parent_code || '—'}</div>
          </div>
        ))}
        {csvParsed.codes.length > 50 && (
          <div className="px-3 py-2 text-center text-[10px] text-[#9CA3AF]">... and {csvParsed.codes.length - 50} more</div>
        )}
      </div>
      <Button onClick={onApply} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="apply-csv-btn">
        <Check className="w-3.5 h-3.5 mr-1.5" /> Apply {csvParsed.count} codes ({csvMode})
      </Button>
    </div>
  );
}

export default function ValuesetDetailPage() {
  const { valuesetId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('edit_elements');
  const [vs, setVs] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [codeForm, setCodeForm] = useState({ code: '', label: '', parent_code: '' });
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvParsed, setCsvParsed] = useState(null);
  const [csvMode, setCsvMode] = useState('merge');
  const [deriveOpen, setDeriveOpen] = useState(false);
  const [deriveName, setDeriveName] = useState('');
  const [includedCodes, setIncludedCodes] = useState(new Set());
  const [includedDirty, setIncludedDirty] = useState(false);
  const fileRef = useRef(null);

  const fetchValueset = useCallback(async () => {
    try {
      const r = await api.get(`/valuesets/${valuesetId}`);
      setVs(r.data);
      setCodes(r.data.codes || []);
      if (r.data.is_derived && r.data.included_codes) {
        setIncludedCodes(new Set(r.data.included_codes));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [valuesetId]);

  useEffect(() => { fetchValueset(); }, [fetchValueset]);

  const handleDrop = (dragCode, targetCode) => {
    if (targetCode === null) {
      // Drop to root
      setCodes(prev => prev.map(c => c.code === dragCode ? { ...c, parent_code: null } : c));
      setDirty(true);
      return;
    }
    if (dragCode === targetCode) return;
    const isDescendant = (parent, child) => {
      const check = (c) => {
        if (c === parent) return true;
        const node = codes.find(x => x.code === c);
        return node?.parent_code ? check(node.parent_code) : false;
      };
      return check(child);
    };
    if (isDescendant(dragCode, targetCode)) {
      toast.error("Can't create circular hierarchy");
      return;
    }
    setCodes(prev => prev.map(c => c.code === dragCode ? { ...c, parent_code: targetCode } : c));
    setDirty(true);
  };

  const handleRemoveParent = (code) => {
    setCodes(prev => prev.map(c => c.code === code ? { ...c, parent_code: null } : c));
    setDirty(true);
  };

  const handleDeleteCode = (code) => {
    setCodes(prev => prev.filter(c => c.code !== code).map(c => c.parent_code === code ? { ...c, parent_code: null } : c));
    setDirty(true);
  };

  const openAddCode = () => { setEditingCode(null); setCodeForm({ code: '', label: '', parent_code: '' }); setAddOpen(true); };

  const openEditCode = (node) => { setEditingCode(node.code); setCodeForm({ code: node.code, label: node.label, parent_code: node.parent_code || '' }); setAddOpen(true); };

  const handleSaveCode = () => {
    if (!codeForm.code.trim() || !codeForm.label.trim()) { toast.error('Code and label required'); return; }
    const parentCode = codeForm.parent_code.trim() || null;
    if (editingCode) {
      setCodes(prev => prev.map(c => c.code === editingCode
        ? { code: codeForm.code.trim(), label: codeForm.label.trim(), parent_code: parentCode }
        : (c.parent_code === editingCode ? { ...c, parent_code: codeForm.code.trim() } : c)
      ));
    } else {
      if (codes.some(c => c.code === codeForm.code.trim())) { toast.error('Code already exists'); return; }
      setCodes(prev => [...prev, { code: codeForm.code.trim(), label: codeForm.label.trim(), parent_code: parentCode }]);
    }
    setAddOpen(false);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/valuesets/${valuesetId}`, { codes });
      toast.success('Valueset saved');
      setDirty(false);
      fetchValueset();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/valuesets/parse-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvParsed(r.data);
      setCsvOpen(true);
    } catch (err) { toast.error('Failed to parse CSV'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleApplyCsv = () => {
    if (!csvParsed?.codes) return;
    if (csvMode === 'replace') {
      setCodes(csvParsed.codes);
    } else {
      const existingCodes = new Set(codes.map(c => c.code));
      const newCodes = csvParsed.codes.filter(c => !existingCodes.has(c.code));
      setCodes(prev => [...prev, ...newCodes]);
    }
    setDirty(true);
    setCsvOpen(false);
    setCsvParsed(null);
    toast.success(`Applied ${csvParsed.codes.length} codes`);
  };

  // ── Derive handlers ──
  const handleDerive = async () => {
    if (!deriveName.trim()) { toast.error('Name required'); return; }
    try {
      const r = await api.post('/valuesets', {
        name: deriveName.trim(),
        description: `Derived from ${vs.name}`,
        parent_valueset_id: valuesetId,
      });
      toast.success('Derived valueset created');
      setDeriveOpen(false);
      navigate(`/valuesets/${r.data.valueset_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const toggleIncluded = (code) => {
    setIncludedCodes(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
    setIncludedDirty(true);
  };

  const handleSaveIncluded = async () => {
    setSaving(true);
    try {
      await api.put(`/valuesets/${valuesetId}/toggle-codes`, { included_codes: [...includedCodes] });
      toast.success('Codes updated');
      setIncludedDirty(false);
      fetchValueset();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const tree = useMemo(() => buildTree(codes), [codes]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!vs) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center text-[#9CA3AF]">Valueset not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="valueset-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/valuesets')} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-to-valuesets">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Valuesets
          </Button>
          <span className="text-[#9CA3AF]">/</span>
          <span className="text-sm font-medium text-[#0A0A0A]">{vs.name}</span>
        </div>

        <div className="border-b border-[#E5E7EB] pb-5 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{vs.name}</h1>
              {vs.description && <p className="text-sm text-[#4B5563]">{vs.description}</p>}
              {vs.is_derived && (
                <p className="text-xs text-[#002FA7] mt-1 flex items-center gap-1">
                  <GitBranch className="w-3 h-3" /> Derived from <button onClick={() => navigate(`/valuesets/${vs.parent_valueset_id}`)} className="underline font-medium">{vs.parent_name}</button>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!vs.is_derived && canEdit && (
                <>
                  <input type="file" ref={fileRef} accept=".csv,.tsv,.txt" onChange={handleFileSelect} className="hidden" />
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/valuesets/csv-template`} download className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F9FAFB] transition-colors" data-testid="csv-template-btn">
                    <Download className="w-3.5 h-3.5" /> CSV Template
                  </a>
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-none text-xs h-8" data-testid="csv-upload-btn">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={openAddCode} className="rounded-none text-xs h-8" data-testid="add-code-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Code
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setDeriveName(`${vs.name} - Subset`); setDeriveOpen(true); }} className="rounded-none text-xs h-8" data-testid="derive-btn">
                    <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Derive
                  </Button>
                </>
              )}
              {!vs.is_derived && dirty && canEdit && (
                <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002585] text-xs h-8" data-testid="save-valueset-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
              {vs.is_derived && includedDirty && canEdit && (
                <Button onClick={handleSaveIncluded} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002585] text-xs h-8" data-testid="save-included-btn">
                  <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </div>

        <StatsRow codes={vs.is_derived ? (vs.resolved_codes || []) : codes} tree={tree} usageCount={vs.usage_count || 0} />

        <div className="flex gap-6">
          <div className="flex-1">
            {vs.is_derived ? (
              /* ── Derived: Checklist of master codes ── */
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">Toggle Codes from Master</h2>
                  <p className="text-[10px] text-[#9CA3AF]">{includedCodes.size} of {(vs.master_codes || []).length} codes included</p>
                </div>
                <div className="border border-[#E5E7EB]" data-testid="derived-checklist">
                  <div className="flex items-center gap-3 py-1.5 px-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <span className="w-5" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] min-w-[60px]">Code</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] flex-1">Label</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] w-16">Parent</span>
                  </div>
                  {(vs.master_codes || []).map(c => {
                    const isIncluded = includedCodes.has(c.code);
                    const indent = c.parent_code ? 24 : 0;
                    return (
                      <div key={c.code} className={`flex items-center gap-3 py-2 px-3 border-b border-[#E5E7EB] last:border-b-0 transition-colors ${isIncluded ? 'bg-white' : 'bg-zinc-50/50'}`} style={{ paddingLeft: `${12 + indent}px` }} data-testid={`check-${c.code}`}>
                        <Checkbox checked={isIncluded} onCheckedChange={() => toggleIncluded(c.code)} className="h-4 w-4 rounded-none" />
                        <span className={`text-xs font-semibold min-w-[60px] ${isIncluded ? 'text-[#0A0A0A]' : 'text-[#9CA3AF]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.code}</span>
                        <span className={`text-sm flex-1 ${isIncluded ? 'text-[#4B5563]' : 'text-[#9CA3AF] line-through'}`}>{c.label}</span>
                        <span className="text-[10px] text-[#9CA3AF] w-16" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.parent_code || '—'}</span>
                      </div>
                    );
                  })}
                </div>
                {includedDirty && <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes</p>}
              </>
            ) : (
              /* ── Master/Standalone: Hierarchy tree editor ── */
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">Code Hierarchy</h2>
                  <p className="text-[10px] text-[#9CA3AF]">Drag codes onto each other to create parent-child relationships</p>
                </div>
                <div className="border border-[#E5E7EB]">
                  <HierarchyTreeView codes={codes} onDrop={handleDrop} onRemoveParent={handleRemoveParent} onDelete={handleDeleteCode} onEdit={openEditCode} />
                </div>
                {dirty && <p className="text-[10px] text-amber-600 mt-2 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes</p>}
                {/* Derived valuesets list */}
                {vs.derived_valuesets && vs.derived_valuesets.length > 0 && (
                  <div className="mt-6">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
                      <GitBranch className="w-3 h-3 inline mr-1" /> Derived Valuesets
                    </h2>
                    <div className="border border-[#E5E7EB]">
                      {vs.derived_valuesets.map(d => (
                        <button key={d.valueset_id} onClick={() => navigate(`/valuesets/${d.valueset_id}`)} className="w-full text-left px-3 py-2 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB] flex items-center justify-between" data-testid={`derived-${d.valueset_id}`}>
                          <span className="text-sm font-medium text-[#002FA7]">{d.name}</span>
                          <span className="text-[10px] text-[#9CA3AF]">{d.included_count} codes</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <UsagesSidebar usages={vs.usages} navigate={navigate} />
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-none max-w-sm" data-testid="code-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editingCode ? 'Edit Code' : 'Add Code'}</DialogTitle><DialogDescription className="sr-only">Add or edit a valueset code</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Code</Label>
              <Input value={codeForm.code} onChange={e => setCodeForm(p => ({ ...p, code: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="code-input" placeholder="e.g., 8140" disabled={!!editingCode} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Label</Label>
              <Input value={codeForm.label} onChange={e => setCodeForm(p => ({ ...p, label: e.target.value }))} className="rounded-none mt-1" data-testid="label-input" placeholder="e.g., Adenocarcinoma" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Parent Code (optional)</Label>
              <Input value={codeForm.parent_code} onChange={e => setCodeForm(p => ({ ...p, parent_code: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="parent-code-input" placeholder="Leave empty for root" />
            </div>
            <Button onClick={handleSaveCode} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-code-btn">
              <Check className="w-3.5 h-3.5 mr-1.5" /> {editingCode ? 'Update' : 'Add'} Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="csv-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Import CSV</DialogTitle><DialogDescription className="sr-only">Preview and import codes from a CSV file</DialogDescription></DialogHeader>
          {csvParsed && <CsvPreview csvParsed={csvParsed} csvMode={csvMode} setCsvMode={setCsvMode} onApply={handleApplyCsv} />}
        </DialogContent>
      </Dialog>

      <Dialog open={deriveOpen} onOpenChange={setDeriveOpen}>
        <DialogContent className="rounded-none max-w-sm" data-testid="derive-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Derived Valueset</DialogTitle><DialogDescription className="sr-only">Create a subset of this valueset</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-[#4B5563]">Creates a new valueset that references this master. You can then toggle which codes from the master are included.</p>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
              <Input value={deriveName} onChange={e => setDeriveName(e.target.value)} className="rounded-none mt-1" data-testid="derive-name-input" placeholder="e.g., Histology - Breast" />
            </div>
            <Button onClick={handleDerive} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="create-derive-btn">
              <GitBranch className="w-3.5 h-3.5 mr-1.5" /> Create Derived Valueset
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
